import React from 'react';
import DOMPurify from 'dompurify';
import useEditableContent from '@/hooks/useEditableContent';

/**
 * Displays admin-editable content. Renders the override value from the DB if any,
 * else falls back to the default value (children).
 *
 * Security: HTML mode runs the value through DOMPurify before injection.
 * Even though only admins can write to PageContent, we still sanitize to defend
 * in depth (e.g., compromised admin session, stored XSS via API).
 */
export default function EditableText({
  contentKey,
  children,
  as: Component = 'span',
  multiline = false,
  allowHtml = false,
  ...rest
}) {
  const defaultValue = typeof children === 'string' ? children : null;
  const value = useEditableContent(contentKey, defaultValue);

  if (value === null || value === undefined) {
    return <Component {...rest}>{children}</Component>;
  }

  if (allowHtml) {
    const sanitized = DOMPurify.sanitize(value, {
      ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'a', 'br', 'p', 'span', 'ul', 'ol', 'li', 'h2', 'h3', 'h4', 'blockquote'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    });
    return <Component {...rest} dangerouslySetInnerHTML={{ __html: sanitized }} />;
  }

  if (multiline) {
    const lines = value.split('\n');
    return (
      <Component {...rest}>
        {lines.map((line, i) => (
          // Stable key combining the line text and its position — safe because
          // re-renders only happen when the whole `value` changes from the DB.
          <React.Fragment key={`${contentKey}-line-${i}-${line.slice(0, 16)}`}>
            {line}
            {i < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </Component>
    );
  }

  return <Component {...rest}>{value}</Component>;
}
